<template>
  <div class="space-y-6">
    <!-- Photo Section -->
    <div class="bg-white rounded-lg shadow p-6">
      <h3 class="text-lg font-semibold mb-4">Profile Photo</h3>
      <div class="flex items-center gap-6">
        <div
          class="relative w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden"
          :class="{ 'cursor-pointer group': canUploadPhoto }"
          @click="canUploadPhoto && triggerPhotoInput()"
        >
          <img v-if="photoPreview" :src="photoPreview" alt="Your photo" class="w-full h-full object-cover" />
          <i v-else class="pi pi-user text-4xl text-gray-400"></i>
          <div
            v-if="canUploadPhoto"
            class="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <i class="pi pi-camera text-white text-xl"></i>
          </div>
        </div>
        <div>
          <p class="font-medium">{{ studentName }}</p>
          <p v-if="student?.matric_no" class="text-sm text-gray-500">{{ student.matric_no }}</p>
          <template v-if="canUploadPhoto">
            <p class="text-xs text-gray-400 mt-1">Click photo to change (max 2MB, JPG/PNG)</p>
            <Button
              v-if="selectedPhotoFile"
              label="Upload Photo"
              icon="pi pi-upload"
              size="small"
              class="mt-2"
              :loading="isUploadingPhoto"
              @click="uploadPhoto"
            />
          </template>
          <p v-else class="text-xs text-gray-400 mt-1">Photo upload is managed by your school admin.</p>
        </div>
        <input ref="photoInputRef" type="file" accept="image/*" class="hidden" @change="onPhotoSelected" />
      </div>
    </div>

    <!-- Contact Info -->
    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold">Contact Information</h3>
        <Button
          v-if="hasContactChanges"
          label="Save Changes"
          icon="pi pi-check"
          size="small"
          :loading="isSavingProfile"
          @click="saveProfile"
        />
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm text-gray-500 mb-1">Email</label>
          <template v-if="emailIsLocked">
            <div class="flex items-center gap-2">
              <span class="font-medium text-sm">{{ student?.email }}</span>
              <i class="pi pi-lock text-gray-400 text-xs"></i>
            </div>
            <p class="text-xs text-gray-400 mt-1">Contact your school admin to change your email.</p>
          </template>
          <template v-else>
            <InputText v-model="contactForm.email" type="email" class="w-full" placeholder="Enter your email address" />
            <p class="text-xs text-gray-400 mt-1">Once saved, your email cannot be changed by you.</p>
          </template>
        </div>
        <div>
          <label class="block text-sm text-gray-500 mb-1">Phone</label>
          <InputText v-model="contactForm.phone" class="w-full" placeholder="Enter your phone number" />
        </div>
      </div>
    </div>

    <StudentProfileFormCard @saved="onProfileFormSaved" />

    <!-- Profile Info (read-only) -->
    <div class="bg-white rounded-lg shadow p-6">
      <h3 class="text-lg font-semibold mb-4">Personal Information</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <span class="text-gray-500 block">Full Name</span>
          <span class="font-medium">{{ studentName }}</span>
        </div>
        <div>
          <span class="text-gray-500 block">Matric No</span>
          <span class="font-medium">{{ student?.matric_no || '-' }}</span>
        </div>
        <div>
          <span class="text-gray-500 block">Gender</span>
          <span class="font-medium">{{ student?.gender || '-' }}</span>
        </div>
        <div>
          <span class="text-gray-500 block">Date of Birth</span>
          <span class="font-medium">{{ student?.dob || '-' }}</span>
        </div>
        <div v-for="row in originRows" :key="row.label"
          :class="{ 'md:col-span-2': row.wide }" :data-testid="`profile-origin-${row.label}`">
          <span class="text-surface-500 block">{{ row.label }}</span>
          <span class="font-medium">{{ row.value }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMutation } from '@vue/apollo-composable';
import { useUserStore } from '@/store/userStore';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { useToast } from 'primevue/usetoast';
import { UPLOAD_MY_PHOTO, UPDATE_MY_PROFILE } from '@/graphql';
import type { UploadMyPhotoMutation } from '@/types/operations';
import { originDisplayRows, pickOriginFields } from '@/utils/studentOrigin';
import { editableProfileKeys } from '@/utils/studentProfileForm';
import { useMyStudentProfileForm } from '@/composables/queries/useMyStudentProfileForm';
import StudentProfileFormCard from '@/components/StudentDashboard/Settings/StudentProfileFormCard.vue';

const toast = useToast();
const userStore = useUserStore();
const { allowStudentPhotoUpload } = useSchoolFeatures();

const student = computed(() => userStore.currentUser as Record<string, unknown>);
// Nationality and the residential address as separate rows, not one lumped "Address" (#574).
// A row the student fills in on the form above is not listed again here.
const { fields: profileFormFields } = useMyStudentProfileForm();
const originRows = computed(() => {
  const onForm = editableProfileKeys(profileFormFields.value);
  return originDisplayRows(pickOriginFields(student.value), { emptyText: '-' }).filter(row => !onForm.has(row.key));
});

// The signed-in user is Apollo's frozen query result: replace it, never write into it.
function onProfileFormSaved(values: Record<string, string | null>) {
  if (userStore.currentUser && Object.keys(values).length) {
    userStore.currentUser = { ...userStore.currentUser, ...values } as typeof userStore.currentUser;
  }
}
const studentName = computed(() => {
  const s = student.value;
  if (!s) return '';
  return [s.first_name, s.other_name, s.surname].filter(Boolean).join(' ');
});

const canUploadPhoto = computed(() => allowStudentPhotoUpload.value);

const emailIsLocked = computed(() => !!student.value?.email);

// Contact form
const contactForm = reactive({
  email: '',
  phone: '',
});

// Follow the stored contact details, but never over an unsaved edit: the
// signed-in student is replaced after any save on this page (the profile card's
// too), and that must not wipe a phone number being typed.
watch(student, (s, previous) => {
  if (!s) return;
  for (const key of ['email', 'phone'] as const) {
    const before = ((previous?.[key] as string) || '');
    if (!previous || contactForm[key] === before) {
      contactForm[key] = (s[key] as string) || '';
    }
  }
}, { immediate: true });

const hasContactChanges = computed(() => {
  const s = student.value;
  if (!s) return false;
  // Compared trimmed: the server trims what it stores.
  const emailChanged = !emailIsLocked.value && contactForm.email.trim() !== ((s.email as string) || '');
  const phoneChanged = contactForm.phone.trim() !== ((s.phone as string) || '');
  return emailChanged || phoneChanged;
});

const isSavingProfile = ref(false);
const { mutate: updateMyProfileMutation } = useMutation(UPDATE_MY_PROFILE);

async function saveProfile() {
  if (!hasContactChanges.value) return;

  const variables: Record<string, string> = {};
  const s = student.value;
  if (!emailIsLocked.value && contactForm.email && contactForm.email !== ((s?.email as string) || '')) {
    variables.email = contactForm.email;
  }
  if (contactForm.phone !== ((s?.phone as string) || '')) {
    variables.phone = contactForm.phone;
  }

  if (!Object.keys(variables).length) return;

  try {
    isSavingProfile.value = true;
    const result = await updateMyProfileMutation(variables);
    const updated = (result?.data as { updateMyProfile?: Record<string, unknown> })?.updateMyProfile;
    if (updated && userStore.currentUser) {
      // The signed-in user is Apollo's frozen query result: replace it rather
      // than write into it, which threw after a save that had succeeded.
      userStore.currentUser = { ...userStore.currentUser, email: updated.email, phone: updated.phone } as typeof userStore.currentUser;
      // Show what was stored (trimmed), so the inputs match it again.
      contactForm.email = (updated.email as string) || '';
      contactForm.phone = (updated.phone as string) || '';
    }
    toast.add({ severity: 'success', summary: 'Profile updated', detail: 'Your contact information has been saved.', life: 3000 });
  } catch (err) {
    const error = err as Error;
    toast.add({ severity: 'error', summary: 'Update failed', detail: error.message || 'Could not update profile.', life: 4000 });
  } finally {
    isSavingProfile.value = false;
  }
}

// Photo upload
const photoInputRef = ref<HTMLInputElement | null>(null);
const photoPreview = computed(() => selectedPreview.value || student.value?.photo || null);
const selectedPreview = ref<string | null>(null);
const selectedPhotoFile = ref<File | null>(null);
const isUploadingPhoto = ref(false);
const { mutate: uploadMyPhotoMutation } = useMutation(UPLOAD_MY_PHOTO);

function triggerPhotoInput() {
  photoInputRef.value?.click();
}

function onPhotoSelected(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  if (file.size > 2 * 1024 * 1024) {
    toast.add({ severity: 'error', summary: 'File too large', detail: 'Photo must be under 2MB.', life: 3000 });
    return;
  }
  selectedPhotoFile.value = file;
  selectedPreview.value = URL.createObjectURL(file);
}

async function uploadPhoto() {
  if (!selectedPhotoFile.value) return;
  try {
    isUploadingPhoto.value = true;
    const result = await uploadMyPhotoMutation({ photo: selectedPhotoFile.value });
    const newPhoto = (result?.data as UploadMyPhotoMutation | undefined)?.uploadMyPhoto?.photo;
    if (newPhoto) {
      selectedPreview.value = newPhoto;
    }
    selectedPhotoFile.value = null;
    toast.add({ severity: 'success', summary: 'Photo updated', detail: 'Your photo has been uploaded.', life: 3000 });
  } catch (err) {
    const error = err as Error;
    toast.add({ severity: 'error', summary: 'Upload failed', detail: error.message || 'Could not upload photo.', life: 4000 });
  } finally {
    isUploadingPhoto.value = false;
  }
}
</script>
